import { useState, useRef, useContext, type ComponentProps } from 'react';
import { useTranslation } from 'next-i18next';
import cx from 'classnames';
import { LoginForm } from '@dx-ui/osc-login';
import type { LoginFormResponse } from '@dx-ui/osc-login';
import { Dialog } from '@dx-ui/osc-dialog-v2';
import { sendReward } from '@dx-ui/framework-conductrics';
import { JoinModal } from '@dx-ui/osc-join-modal-button';

import { NavItemVisual, NavItemWithModal } from '../../nav-items';
import { SpeechBubble } from '../../../brand-header-body/speech-bubble';
import { useHeaderContext } from '../../../context/header-context';
import { sendInteractionReward, SpeechBubbleContext } from '../../../header.utilities';

export type UserNavModalSharedProps = {
  defaultOptions: ComponentProps<typeof LoginForm>;
  loginOptions?: Partial<ComponentProps<typeof LoginForm>>;
  buttonLabel: string;
  onClick?: () => void;
  navItemClassName?: string;
  showSpeechBubble?: boolean;
  onDialogOpen?: () => void;
  shouldOpenJoinInPage?: boolean;
  locale?: string;
};

export type SpeechBubbleExtras = {
  speechBubbleVariant: string;
  buttonClassName?: string;
};

export const UserNavItemWithModalDefaultVariant = ({
  defaultOptions,
  loginOptions,
  buttonLabel,
  onClick,
  navItemClassName,
  showSpeechBubble,
  onDialogOpen,
  shouldOpenJoinInPage,
  locale = 'en',
}: UserNavModalSharedProps) => {
  if (showSpeechBubble) {
    return (
      <SpeechBubbleSignInVariant
        defaultOptions={defaultOptions}
        loginOptions={loginOptions}
        buttonLabel={buttonLabel}
        onClick={onClick}
        navItemClassName={navItemClassName}
        onDialogOpen={onDialogOpen}
        shouldOpenJoinInPage={shouldOpenJoinInPage}
        locale={locale}
      />
    );
  }

  return (
    <NavItemWithModal
      section="user"
      onLoginAttempt={defaultOptions.onLoginAttempt}
      onClick={onClick}
      buttonIcon="user-circle"
      buttonLabel={buttonLabel}
      navItemClassName={navItemClassName}
      shouldOpenJoinInPage={shouldOpenJoinInPage}
      locale={locale}
    >
      <LoginForm {...defaultOptions} {...loginOptions} />
    </NavItemWithModal>
  );
};

const SpeechBubbleSignInVariant = ({
  defaultOptions,
  loginOptions,
  buttonLabel,
  onClick,
  navItemClassName,
  onDialogOpen,
  shouldOpenJoinInPage,
  locale = 'en',
}: Omit<UserNavModalSharedProps, 'showSpeechBubble'>) => {
  const { onDismiss } = useContext(SpeechBubbleContext);
  const [showDialog, setShowDialog] = useState(false);
  const [showJoinModal, setShowJoinModal] = useState(false);
  const [showSpeechBubble, setShowSpeechBubble] = useState(true);
  const {
    user: { analytics },
  } = useHeaderContext();
  const [t] = useTranslation('osc-login');
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [speechBubbleContainer, setSpeechBubbleContainer] = useState<HTMLDivElement | undefined>(
    undefined
  );

  const close = () => {
    setShowDialog(false);
  };

  const handleLogin = (response: LoginFormResponse) => {
    defaultOptions.onLoginAttempt?.(response);
    if (!response.error) {
      sendReward('g-IqzR2S0Ai5');
      close();
    }
  };

  const closeSpeechBubble = () => {
    setShowSpeechBubble(false);
    buttonRef.current?.focus();
  };

  const handleSpeechBubbleDismiss = () => {
    onDismiss?.();
    closeSpeechBubble();
  };

  const setContainerRef = (node: HTMLDivElement) => {
    if (node) {
      setSpeechBubbleContainer(node);
    }
  };

  const onJoinClick = () => {
    setShowJoinModal(true);
    close();
  };

  const baseLoginFormConfig = loginOptions?.loginFormConfig ?? defaultOptions.loginFormConfig;
  const loginFormConfig = !shouldOpenJoinInPage
    ? {
        ...(baseLoginFormConfig ?? {
          showCreatePasswordLink: false,
          showForgetInfoLink: true,
          showJoinLink: true,
          showSecureInfoLink: false,
        }),
        onJoinClick,
        joinFromModal: true,
      }
    : baseLoginFormConfig;

  return (
    <>
      {!shouldOpenJoinInPage ? (
        <JoinModal
          analytics={analytics}
          isOpen={showJoinModal}
          language={locale}
          onLoginAttempt={defaultOptions.onLoginAttempt}
          onDismiss={() => {
            setShowJoinModal(false);
            setTimeout(() => buttonRef.current?.focus(), 100);
          }}
          onSuccessfulLogin={() => {
            setShowJoinModal(false);
            setTimeout(() => buttonRef.current?.focus(), 1000);
          }}
        />
      ) : null}
      <li
        data-testid="with-modal"
        className={cx(
          'osc-nav-list-item text--sm group flex h-full items-center border-t-4 border-t-transparent px-1',
          navItemClassName
        )}
      >
        <SpeechBubble
          closeSpeechBubble={handleSpeechBubbleDismiss}
          showSpeechBubble={showSpeechBubble}
          containerRef={speechBubbleContainer}
          onInlineButtonClick={() => {
            closeSpeechBubble();
            setShowDialog(true);
            onDialogOpen?.();
          }}
        >
          <button
            type="button"
            className="text--sm group flex h-full flex-1 items-center self-stretch whitespace-nowrap px-1"
            data-testid="with-modal"
            ref={buttonRef}
            aria-describedby="sign-in-note"
            onClick={() => {
              sendInteractionReward();
              onClick?.();
              setShowDialog(true);
              setShowSpeechBubble(false);
              onDialogOpen?.();
            }}
          >
            <NavItemVisual label={buttonLabel} icon="user-circle" section="user" />
          </button>
        </SpeechBubble>
      </li>
      <Dialog isOpen={showDialog && !showJoinModal} onDismiss={close} size="md" title={t('signIn')}>
        <LoginForm
          {...defaultOptions}
          {...loginOptions}
          analytics={analytics}
          isLegacySignInAnalyticsEnabled={analytics?.isLegacySignInAnalyticsEnabled}
          loginFormConfig={loginFormConfig}
          onLoginAttempt={handleLogin}
          isInModal={true}
        />
      </Dialog>
      <div ref={setContainerRef} />
    </>
  );
};
